import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ChevronDown, User, CarFront, ShieldAlert, Shield, Award, Wrench,
  Sparkles, Compass, QrCode, Menu, X, Calculator, ShieldCheck, FileBadge, Trophy,
} from "lucide-react";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { CountrySelector } from "@/components/CountrySelector";
import { useAuth } from "@/context/AuthContext";
import { Flag } from "@/context/SiteFlagsContext";
import { RedDesktopMenu, RedMobileSection } from "@/components/pending-services/RedMenu";
import { ProximosDesktop, ProximosMobile } from "@/components/ProximosServicios";

export const Navbar = ({ transparent = false }) => {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [open, setOpen] = useState(false);
  const [openProd, setOpenProd] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const menuRef = useRef(null);
  const prodRef = useRef(null);

  // Lock scroll while mobile panel is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  useEffect(() => {
    if (!open && !openProd) return;
    const onDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
      if (prodRef.current && !prodRef.current.contains(e.target)) setOpenProd(false);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [open, openProd]);

  const closeMobile = () => setMobileOpen(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    if (!transparent) return;
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [transparent]);
  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow] duration-300 ${
        transparent
          ? scrolled ? "bg-[#13315C]/95 backdrop-blur-xl shadow-lg shadow-black/10" : "bg-[#13315C]"
          : "bg-white/80 backdrop-blur-xl border-b border-slate-200"
      }`}
      data-testid="public-navbar"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" data-testid="nav-home-link" className="shrink-0">
          <Brand light={transparent} />
        </Link>
        <nav className="hidden md:flex items-center gap-x-4 lg:gap-x-5 text-[13px] font-semibold whitespace-nowrap">
          <div
            className="relative"
            ref={prodRef}
            onMouseEnter={() => setOpenProd(true)}
            onMouseLeave={() => setOpenProd(false)}
          >
            <button
              onClick={() => setOpenProd(true)}
              className={`inline-flex items-center gap-1 ${transparent ? "text-white/80 hover:text-white" : "text-slate-600 hover:text-[#13315C]"}`}
              data-testid="nav-productos-btn"
            >
              Productos <ChevronDown size={14} className={`transition-transform ${openProd ? "rotate-180" : ""}`} />
            </button>
            {openProd && (
              <div className="absolute left-0 top-full pt-2 w-[420px] z-50" data-testid="productos-menu" onClick={() => setOpenProd(false)}>
                <div className="rounded-2xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
                  <ProdItem to="/nueva-transferencia" icon={<Trophy size={18} />} title="Transferencia digital" desc="Producto estrella · Tu transferencia 100% digital en 72 horas" testid="prod-transferencias" />
                  <ProdItem to="/#informe" icon={<FileBadge size={18} />} title="Informe 360" desc="Historial legal del vehículo con dictamen" testid="prod-informe-360" />
                  {/* servicio pendiente — reactivar vía flag en Cerebro (pagoProtegido) */}
                  <Flag name="pagoProtegido"><ProdItem to="/pago-protegido" icon={<Shield size={18} />} title="Pago Protegido" desc="Escrow legal · Compra sin riesgo de estafa" testid="prod-pago-protegido" /></Flag>
                  {/* servicio pendiente — reactivar vía flag en Cerebro (selloGarantizado) */}
                  <Flag name="selloGarantizado"><ProdItem to="/sello-garantizado" icon={<Award size={18} />} title="Sello Garantizado" desc="Cobertura legal hasta $1.000.000 si algo falla" testid="prod-sello-garantizado" /></Flag>
                  {/* servicio pendiente — reactivar vía flag en Cerebro (inspeccionMecanica) */}
                  <Flag name="inspeccionMecanica"><ProdItem to="/inspeccion-mecanica" icon={<Wrench size={18} />} title="Inspección Mecánica Móvil" desc="Mecánico certificado a domicilio en 24h" testid="prod-inspeccion" /></Flag>
                  <ProdItem to="/precompra-publico/calcular" icon={<Calculator size={18} />} title="¿Cuánto vale el auto que veo?" desc="Estima impacto en CLP gratis con IA · Sin registro" testid="prod-lead-magnet" />
                  {/* servicio pendiente — reactivar vía flag en Cerebro (ventaEn60Segundos) */}
                  <Flag name="ventaEn60Segundos"><ProdItem to="/vende-en-60-segundos" icon={<Sparkles size={18} />} title="Vende en 60 segundos" desc="Sube tu auto · IA busca compradores calificados" testid="prod-vende" /></Flag>
                  {/* servicio pendiente — reactivar vía flag en Cerebro (centroVerificacion) */}
                  <Flag name="centroVerificacion"><ProdItem to="/verificacion" icon={<ShieldCheck size={18} />} title="Centro de Verificación" desc="KYC · AML con IA · TrustScore 0-1000" testid="prod-verificacion" /></Flag>
                </div>
              </div>
            )}
          </div>
          <a href="/#como-funciona" className={transparent ? "text-white/80 hover:text-white" : "text-slate-600 hover:text-[#13315C]"}>Cómo funciona</a>
          <Link to="/como-operamos" className={transparent ? "text-white/80 hover:text-white" : "text-slate-600 hover:text-[#13315C]"} data-testid="nav-como-operamos-link">Sobre nosotros</Link>

          {/* Mega-menú LA RED — servicio pendiente — reactivar vía flag en Cerebro (enLaRedTodo) */}
          <Flag name="enLaRedTodo"><RedDesktopMenu transparent={transparent} ProdItem={ProdItem} /></Flag>

          <Link to="/precios" className={transparent ? "text-white/80 hover:text-white" : "text-slate-600 hover:text-[#13315C]"} data-testid="nav-precios-link">Precios</Link>
          <Link to="/vendedores" className={transparent ? "text-white/80 hover:text-white" : "text-slate-600 hover:text-[#13315C]"} data-testid="nav-vendedores-link">Asesores</Link>
          {/* servicio pendiente — reactivar vía flag en Cerebro (compararPrecios) */}
          <Flag name="compararPrecios"><Link to="/comparar" className={transparent ? "text-white/80 hover:text-white" : "text-slate-600 hover:text-[#13315C]"} data-testid="nav-comparar-link">Comparar</Link></Flag>
        </nav>
        <div className="flex items-center gap-2 sm:gap-3">
          <CountrySelector light={transparent} />
          {/* servicio pendiente — reactivar vía flag en Cerebro (escanearQr) */}
          <Flag name="escanearQr"><Link
            to="/escanear"
            title="Escanear QR de un documento Transfiérelo"
            aria-label="Escanear QR"
            className={`hidden sm:inline-flex items-center justify-center w-9 h-9 rounded-md border transition active:scale-[0.94] ${
              transparent
                ? "border-white/20 text-white/80 hover:text-white hover:border-white/40"
                : "border-slate-200 text-slate-500 hover:text-[#13315C] hover:border-[#F5B301]"
            }`}
            data-testid="nav-escanear-link"
          >
            <QrCode size={18} />
          </Link></Flag>
          <ProximosDesktop transparent={transparent} />
          {user ? (
            <Button onClick={() => navigate("/dashboard")} className="hidden sm:inline-flex bg-[#13315C] hover:bg-[#1C4076] text-white rounded-md h-10 active:scale-[0.97] transition-transform" data-testid="nav-dashboard-btn">
              Mi panel
            </Button>
          ) : (
            <div
              className="relative hidden sm:block"
              ref={menuRef}
              onMouseEnter={() => setOpen(true)}
              onMouseLeave={() => setOpen(false)}
            >
              <Button
                onClick={() => setOpen(true)}
                className="bg-[#13315C] hover:bg-[#1C4076] text-white rounded-md h-11 sm:h-10 active:scale-[0.97] transition-transform"
                data-testid="nav-login-btn"
                aria-haspopup="true"
                aria-expanded={open}
              >
                Ingresar <ChevronDown size={16} className={`ml-1 transition-transform ${open ? "rotate-180" : ""}`} />
              </Button>
              {open && (
                <div className="absolute right-0 top-full pt-2 w-64" data-testid="login-menu">
                  <div className="rounded-xl border border-slate-200 bg-white shadow-xl overflow-hidden">
                    <button
                      onClick={() => login("persona")}
                      className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors"
                      data-testid="login-persona-btn"
                    >
                      <span className="h-9 w-9 rounded-md bg-[#10B981]/10 flex items-center justify-center shrink-0"><User size={18} className="text-[#10B981]" /></span>
                      <span>
                        <span className="block text-sm font-semibold text-[#13315C]">Ingresar como persona</span>
                        <span className="block text-xs text-slate-500">Compra o vende tu vehículo</span>
                      </span>
                    </button>
                    <button
                      onClick={() => login("automotora")}
                      className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors border-t border-slate-100"
                      data-testid="login-automotora-btn"
                    >
                      <span className="h-9 w-9 rounded-md bg-[#13315C]/10 flex items-center justify-center shrink-0"><CarFront size={18} className="text-[#13315C]" /></span>
                      <span>
                        <span className="block text-sm font-semibold text-[#13315C]">Ingresar como automotora <span className="ml-1 text-[9px] font-black tracking-wide bg-[#F5B301] text-[#13315C] px-1 py-0.5 rounded align-middle">PRO</span></span>
                        <span className="block text-xs text-slate-500">Inventario, retomas y vitrina</span>
                      </span>
                    </button>
                    <button
                      onClick={() => login("taller")}
                      className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors border-t border-slate-100"
                      data-testid="login-taller-btn"
                    >
                      <span className="h-9 w-9 rounded-md bg-[#F5B301]/15 flex items-center justify-center shrink-0"><Wrench size={18} className="text-[#9a7100]" /></span>
                      <span>
                        <span className="block text-sm font-semibold text-[#13315C]">Ingresar como taller mecánico</span>
                        <span className="block text-xs text-slate-500">Cola, certificados QR y revenue</span>
                      </span>
                    </button>
                    <Link
                      to="/admin/login"
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-slate-50 transition-colors border-t border-slate-100"
                      data-testid="login-admin-link"
                    >
                      <ShieldAlert size={16} className="text-slate-400 shrink-0" />
                      <span className="text-xs font-semibold text-slate-500">Acceso administradores</span>
                    </Link>
                  </div>
                </div>
              )}
            </div>
          )}
          {/* Hamburger · solo mobile */}
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={mobileOpen ? "Cerrar menú" : "Abrir menú"}
            aria-expanded={mobileOpen}
            className={`md:hidden inline-flex items-center justify-center w-11 h-11 rounded-md border active:scale-[0.94] transition ${
              transparent
                ? "border-white/20 text-white"
                : "border-slate-200 text-[#13315C]"
            }`}
            data-testid="nav-mobile-toggle"
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <div
            className="md:hidden fixed inset-0 top-16 bg-black/40 backdrop-blur-sm"
            onClick={closeMobile}
            data-testid="nav-mobile-overlay"
          />
          <div
            className="md:hidden fixed inset-x-0 top-16 bg-white border-b border-slate-200 shadow-2xl max-h-[calc(100vh-4rem)] overflow-y-auto tf-fade-up"
            data-testid="nav-mobile-panel"
          >
            <div className="px-5 py-5 space-y-1">
              <MobileSection title="Productos" defaultOpen>
                <MobileLink to="/nueva-transferencia" onClick={closeMobile} icon={<Trophy size={16} />} label="Transferencia digital · Producto estrella" testid="mob-prod-transferencias" />
                <MobileLink to="/#informe" onClick={closeMobile} icon={<FileBadge size={16} />} label="Informe 360" testid="mob-prod-informe-360" />
                {/* servicio pendiente — reactivar vía flag en Cerebro (pagoProtegido) */}
                <Flag name="pagoProtegido"><MobileLink to="/pago-protegido" onClick={closeMobile} icon={<Shield size={16} />} label="Pago Protegido" testid="mob-prod-pago" /></Flag>
                {/* servicio pendiente — reactivar vía flag en Cerebro (selloGarantizado) */}
                <Flag name="selloGarantizado"><MobileLink to="/sello-garantizado" onClick={closeMobile} icon={<Award size={16} />} label="Sello Garantizado" testid="mob-prod-sello" /></Flag>
                {/* servicio pendiente — reactivar vía flag en Cerebro (inspeccionMecanica) */}
                <Flag name="inspeccionMecanica"><MobileLink to="/inspeccion-mecanica" onClick={closeMobile} icon={<Wrench size={16} />} label="Inspección Mecánica" testid="mob-prod-inspeccion" /></Flag>
                <MobileLink to="/precompra-publico/calcular" onClick={closeMobile} icon={<Calculator size={16} />} label="¿Cuánto vale el auto que veo?" testid="mob-prod-lead-magnet" />
                {/* servicio pendiente — reactivar vía flag en Cerebro (ventaEn60Segundos) */}
                <Flag name="ventaEn60Segundos"><MobileLink to="/vende-en-60-segundos" onClick={closeMobile} icon={<Sparkles size={16} />} label="Vende en 60 segundos" testid="mob-prod-vende" /></Flag>
                {/* servicio pendiente — reactivar vía flag en Cerebro (centroVerificacion) */}
                <Flag name="centroVerificacion"><MobileLink to="/verificacion" onClick={closeMobile} icon={<ShieldCheck size={16} />} label="Centro de Verificación" testid="mob-prod-verificacion" /></Flag>
              </MobileSection>

{/* servicio pendiente — reactivar vía flag en Cerebro (enLaRedTodo) */}
              <Flag name="enLaRedTodo"><RedMobileSection closeMobile={closeMobile} MobileSection={MobileSection} MobileLink={MobileLink} /></Flag>

              <div className="pt-2 mt-2 border-t border-slate-100 space-y-1">
                <MobileLink to="/como-operamos" onClick={closeMobile} icon={<Compass size={16} />} label="Sobre nosotros" testid="mob-nav-como-operamos" />
                <MobileLink to="/precios" onClick={closeMobile} icon={<Sparkles size={16} />} label="Precios" testid="mob-nav-precios" />
                {/* servicio pendiente — reactivar vía flag en Cerebro (compararPrecios) */}
                <Flag name="compararPrecios"><MobileLink to="/comparar" onClick={closeMobile} icon={<CarFront size={16} />} label="Comparar planes" testid="mob-nav-comparar" /></Flag>
                {/* servicio pendiente — reactivar vía flag en Cerebro (escanearQr) */}
                <Flag name="escanearQr"><MobileLink to="/escanear" onClick={closeMobile} icon={<QrCode size={16} />} label="Escanear QR" testid="mob-nav-escanear" /></Flag>
                <ProximosMobile onClose={closeMobile} />
              </div>

              {user && (
                <div className="pt-4 mt-3 border-t border-slate-100 space-y-1">
                  <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400 px-2">Mi cuenta</p>
                  <MobileLink to="/dashboard" onClick={closeMobile} icon={<User size={16} />} label="Mi panel" testid="mob-mi-panel" />
                  {(user.role === "admin" || user.role === "operador") && (
                    <MobileLink to="/admin" onClick={closeMobile} icon={<ShieldAlert size={16} />} label="Panel de administración" testid="mob-admin-panel" />
                  )}
                </div>
              )}

              {!user && (
                <div className="pt-4 mt-3 border-t border-slate-100 space-y-2">
                  <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400 px-2">Acceder</p>
                  <button
                    onClick={() => { closeMobile(); login("persona"); }}
                    className="w-full flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-slate-50 active:bg-slate-100 text-left"
                    data-testid="mob-login-persona"
                  >
                    <span className="h-9 w-9 rounded-md bg-[#10B981]/10 flex items-center justify-center shrink-0"><User size={16} className="text-[#10B981]" /></span>
                    <span className="text-sm font-semibold text-[#13315C]">Como persona</span>
                  </button>
                  <button
                    onClick={() => { closeMobile(); login("automotora"); }}
                    className="w-full flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-slate-50 active:bg-slate-100 text-left"
                    data-testid="mob-login-automotora"
                  >
                    <span className="h-9 w-9 rounded-md bg-[#13315C]/10 flex items-center justify-center shrink-0"><CarFront size={16} className="text-[#13315C]" /></span>
                    <span className="text-sm font-semibold text-[#13315C]">Como automotora <span className="ml-1 text-[8px] font-black tracking-wide bg-[#F5B301] text-[#13315C] px-1 py-0.5 rounded">PRO</span></span>
                  </button>
                  <button
                    onClick={() => { closeMobile(); login("taller"); }}
                    className="w-full flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-slate-50 active:bg-slate-100 text-left"
                    data-testid="mob-login-taller"
                  >
                    <span className="h-9 w-9 rounded-md bg-[#F5B301]/15 flex items-center justify-center shrink-0"><Wrench size={16} className="text-[#9a7100]" /></span>
                    <span className="text-sm font-semibold text-[#13315C]">Como taller mecánico</span>
                  </button>
                  <Link
                    to="/admin/login"
                    onClick={closeMobile}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-50 active:bg-slate-100 text-left"
                    data-testid="mob-login-admin"
                  >
                    <ShieldAlert size={15} className="text-slate-400 shrink-0" />
                    <span className="text-xs font-semibold text-slate-500">Acceso administradores</span>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </header>
  );
};

const MobileSection = ({ title, children, defaultOpen = false }) => {
  const [openSec, setOpenSec] = useState(defaultOpen);
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpenSec((v) => !v)}
        className="w-full flex items-center justify-between px-2 py-2.5 text-left text-[11px] font-bold uppercase tracking-widest text-slate-500"
      >
        {title}
        <ChevronDown size={14} className={`transition-transform ${openSec ? "rotate-180" : ""}`} />
      </button>
      {openSec && <div className="space-y-0.5">{children}</div>}
    </div>
  );
};

const MobileLink = ({ to, icon, label, testid, onClick }) => (
  <Link
    to={to}
    onClick={onClick}
    data-testid={testid}
    className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-slate-50 active:bg-slate-100 transition-colors min-h-[44px]"
  >
    <span className="grid h-8 w-8 place-items-center rounded-md bg-[#F5B301]/10 text-[#13315C] shrink-0">{icon}</span>
    <span className="text-sm font-semibold text-[#13315C]">{label}</span>
  </Link>
);

const ProdItem = ({ to, icon, title, desc, testid }) => (
  <Link
    to={to}
    data-testid={testid}
    className="flex items-start gap-3 px-4 py-3 hover:bg-slate-50 border-b border-slate-100 last:border-b-0 transition-colors"
  >
    <span className="grid h-9 w-9 place-items-center rounded-lg bg-[#F5B301]/10 text-[#13315C] shrink-0">{icon}</span>
    <span>
      <span className="block text-sm font-bold text-[#13315C]">{title}</span>
      <span className="block text-xs text-slate-500 mt-0.5">{desc}</span>
    </span>
  </Link>
);
